import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  EquipmentType, LastPaymentType, ServicesType, SubscriptionType, TariffType,
} from '@src/store/ducks/user/types'
import { MessageType, UserAddressType, UserType } from '@src/types/user'
import { PersonalDataType } from '@src/api/user-api'

const currentAddress = localStorage.getItem('currentAccount')

export const initialState = {
  user: {
    username: '',
    address: '',
    phones: [],
    phoneNotify: '',
    emails: [],
    isSubscriber: false,
    isName: false,
    fields: [],
  } as UserType,
  listAddresses: [] as UserAddressType[],
  currentAddresses: currentAddress ? +currentAddress : currentAddress as number | null,
  isLoadingListAddresses: false,
  isLoadingChangePhone: false,
  isLoadingChangePhoneNotification: false,
  isSuccessChangePhone: false,
  isSuccessGameForm: false,
  isSuccessTerminationAgreements: false,
  services: {
    equipment: [] as Array<EquipmentType>,
    services: [] as Array<SubscriptionType>,
    subscriptions: [] as Array<SubscriptionType>,
    futureServices: [] as Array<SubscriptionType>,
    tariff: [] as Array<TariffType>,
    newTariff: [] as Array<TariffType>,
  },
  salary: 0,
  sumToPay: 0,
  lastPayment: {
    date: '',
    summ: 0,
  } as LastPaymentType,
  terminationCauses: [] as string[],
  messagesForUsers: [] as MessageType[],
  userId: 0 as number,
  userUnreadMessages: 0 as number,
  personalData: null as PersonalDataType | null,
  isLoadingPersonalData: false,
  isSuccessPersonalData: false,
}
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser(state, { payload }: PayloadAction<UserType>) {
      state.user = payload
      state.user.emails = state.user.emails.filter((item) => item !== ' ')
    },
    setListAddresses(state, { payload }: PayloadAction<UserAddressType[]>) {
      state.listAddresses = payload
    },
    setIsLoadingListAddresses(state, { payload }: PayloadAction<boolean>) {
      state.isLoadingListAddresses = payload
    },
    setCurrentAddresses(state, { payload }: PayloadAction<number>) {
      localStorage.setItem('currentAccount', `${payload}`)
      state.currentAddresses = payload
    },
    setPhoneNotify(state, { payload }: PayloadAction<string>) {
      state.user.phoneNotify = payload
    },
    updatePhone(state, { payload }: PayloadAction<{oldPhone: string, phone: string}>) {
      state.user.phones = state.user.phones.map((item) => (item === payload.oldPhone ? payload.phone : item))
    },
    addPhone(state, { payload }: PayloadAction<string>) {
      state.user.phones.push(payload)
    },
    setIsLoadingChangePhone(state, { payload }: PayloadAction<boolean>) {
      state.isLoadingChangePhone = payload
    },
    setIsSuccessGameForm(state, { payload }: PayloadAction<boolean>) {
      state.isSuccessGameForm = payload
    },
    setIsLoadingChangePhoneNotification(state, { payload }: PayloadAction<boolean>) {
      state.isLoadingChangePhoneNotification = payload
    },
    setIsSuccessChangePhone(state, { payload }: PayloadAction<boolean>) {
      state.isSuccessChangePhone = payload
    },
    setIsSuccessTerminationAgreements(state, { payload }: PayloadAction<boolean>) {
      state.isSuccessTerminationAgreements = payload
    },
    setServices(state, { payload }: PayloadAction<ServicesType>) {
      let salary = 0

      salary += payload.tariff.reduce((sum, current) => sum + current.price, 0)
      salary += payload.services.reduce((sum, current) => sum + current.price, 0)
      salary += payload.subscriptions.reduce((sum, current) => sum + current.price, 0)

      state.salary = +salary.toFixed(2)
      state.services = { ...payload, tariff: payload.tariff.sort((a, b) => b.price - a.price) }
    },
    setSumToPay(state, { payload }: PayloadAction<number>) {
      state.sumToPay = payload
    },
    setLastPayment(state, { payload }: PayloadAction<LastPaymentType>) {
      state.lastPayment = payload
    },
    updateEmail(state, { payload }: PayloadAction<{oldEmail: string, email: string}>) {
      state.user.emails = state.user.emails.map((item) => (item === payload.oldEmail ? payload.email : item))
    },
    addEmail(state, { payload }: PayloadAction<string>) {
      state.user.emails.push(payload)
    },
    setTerminationCauses(state, { payload }: PayloadAction<string[]>) {
      state.terminationCauses = payload
    },
    setMessagesForUsers(state, { payload }: PayloadAction<MessageType[]>) {
      state.messagesForUsers = payload
    },
    setUserId(state, { payload }: PayloadAction<number>) {
      state.userId = payload
    },
    setUserUnreadMessages(state, { payload }: PayloadAction<number>) {
      state.userUnreadMessages = payload
    },
    setPersonalData(state, { payload }: PayloadAction<PersonalDataType>) {
      state.personalData = payload
    },
    setIsLoadingPersonalData(state, { payload }: PayloadAction<boolean>) {
      state.isLoadingPersonalData = payload
    },
    setIsSuccessPersonalData(state, { payload }: PayloadAction<boolean>) {
      state.isSuccessPersonalData = payload
    },
  },
})
export type UserStateType = typeof initialState
export const {
  setUser,
  setListAddresses,
  setIsLoadingListAddresses,
  setCurrentAddresses,
  setIsLoadingChangePhone,
  setIsLoadingChangePhoneNotification,
  setIsSuccessChangePhone,
  setIsSuccessGameForm,
  setPhoneNotify,
  updateEmail,
  addEmail,
  setServices,
  setSumToPay,
  setLastPayment,
  updatePhone,
  addPhone,
  setIsSuccessTerminationAgreements,
  setTerminationCauses,
  setMessagesForUsers,
  setUserId,
  setUserUnreadMessages,
  setPersonalData,
  setIsLoadingPersonalData,
  setIsSuccessPersonalData,
} = userSlice.actions
export const userReducer = userSlice.reducer
